/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Trial Balance Page - SuperAdmin Journal Ledger Audit
 * ============================================================================
 *
 * Displays the trial balance report for a selected organization as of a
 * specific date. Shows account-level debit/credit totals, net balances,
 * and a balanced/imbalanced indicator.
 *
 * ============================================================================
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Scale,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Building2,
  Calendar,
  TrendingUp,
  TrendingDown,
  Equal,
} from 'lucide-react';
import { useTrialBalance } from '@/hooks/api/useJournalLedger';
import { useOrganizations } from '@/hooks/api/useOrganizations';
import type { AccountBalance } from '@/services/superadmin/journal-ledger.service';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatMoney(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const ACCOUNT_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  CASH_REGISTER: { bg: 'var(--color-success-light)', text: 'var(--color-success)' },
  BANK_ACCOUNT: { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
  PARTNER_WALLET: { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
  ORG_REVENUE: { bg: 'var(--color-brand-primary)', text: '#fff' },
  ORG_EXPENSE: { bg: 'var(--color-error-light)', text: 'var(--color-error)' },
  TAX_COLLECTED: { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
  TAX_PAID: { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
  ACCOUNTS_RECEIVABLE: { bg: 'var(--color-success-light)', text: 'var(--color-success)' },
  ACCOUNTS_PAYABLE: { bg: 'var(--color-error-light)', text: 'var(--color-error)' },
  INVENTORY: { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
  DISCOUNT: { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
  COMMISSION: { bg: 'var(--color-brand-primary)', text: '#fff' },
  PLATFORM_REVENUE: { bg: 'var(--color-brand-primary)', text: '#fff' },
  PLATFORM_COMMISSION: { bg: 'var(--color-brand-primary)', text: '#fff' },
  SUSPENSE: { bg: 'var(--color-error-light)', text: 'var(--color-error)' },
};

const DEFAULT_TYPE_COLOR = { bg: 'var(--color-bg-tertiary)', text: 'var(--color-text-secondary)' };

function getAccountTypeColor(type: string) {
  return ACCOUNT_TYPE_COLORS[type] || DEFAULT_TYPE_COLOR;
}

// ── Component ────────────────────────────────────────────────────────────────

export function TrialBalancePage() {
  const { t } = useTranslation('platform');

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [asOfDate, setAsOfDate] = useState('');

  // Fetch first page of organizations (up to 100)
  const { data: orgsData, isLoading: orgsLoading } = useOrganizations({ page: 0, size: 100 });
  const organizations = useMemo(() => {
    if (!orgsData) return [];
    return orgsData.content ?? [];
  }, [orgsData]);

  // Fetch trial balance when org is selected
  const {
    data: trialBalance,
    isLoading: tbLoading,
    error: tbError,
    refetch,
  } = useTrialBalance(selectedOrgId, asOfDate || undefined);

  const isLoading = tbLoading && !!selectedOrgId;

  const grandNetBalance = useMemo(() => {
    if (!trialBalance) return 0;
    return trialBalance.grandTotalDebit - trialBalance.grandTotalCredit;
  }, [trialBalance]);

  // ── Loading state ──────────────────────────────────────────────────────────

  if (orgsLoading) {
    return (
      <div className="tb-page">
        <div className="tb-center-state">
          <RefreshCw className="tb-spin" size={24} />
          <span>{t('platform:journal.trialBalance.loadingText')}</span>
        </div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="tb-page">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="tb-header">
        <div className="tb-header-left">
          <h1 className="tb-title">
            <Scale className="tb-title-icon" size={22} />
            {t('platform:journal.trialBalance.title')}
          </h1>
          {trialBalance && (
            <div className={`tb-balanced-pill ${trialBalance.balanced ? 'tb-balanced-pill--yes' : 'tb-balanced-pill--no'}`}>
              {trialBalance.balanced ? (
                <>
                  <CheckCircle size={14} />
                  {t('platform:journal.trialBalance.stats.balanced')}
                </>
              ) : (
                <>
                  <XCircle size={14} />
                  {t('platform:journal.trialBalance.imbalancedLabel')}
                </>
              )}
            </div>
          )}
        </div>
        {selectedOrgId && (
          <button className="tb-btn" onClick={() => refetch()}>
            <RefreshCw size={14} /> {t('platform:journal.trialBalance.refresh')}
          </button>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="tb-filters tb-anim-1">
        <div className="tb-filter-group">
          <label className="tb-filter-label">
            <Building2 size={14} />
            {t('platform:journal.trialBalance.organizationLabel')}
          </label>
          <select
            className="tb-select"
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
          >
            <option value="">{t('platform:journal.trialBalance.selectOrg')}</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.code})
              </option>
            ))}
          </select>
        </div>

        <div className="tb-filter-group">
          <label className="tb-filter-label">
            <Calendar size={14} />
            {t('platform:journal.trialBalance.asOf')}
          </label>
          <input
            type="date"
            className="tb-date-input"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
      </div>

      {/* ── No org selected state ──────────────────────────────────────── */}
      {!selectedOrgId && (
        <div className="tb-empty-state tb-anim-2">
          <Building2 size={40} className="tb-empty-icon" />
          <p className="tb-empty-text">{t('platform:journal.trialBalance.selectOrgPrompt')}</p>
        </div>
      )}

      {/* ── Loading trial balance ──────────────────────────────────────── */}
      {isLoading && (
        <div className="tb-center-state tb-anim-2">
          <RefreshCw className="tb-spin" size={24} />
          <span>{t('platform:journal.trialBalance.loadingBalanceText')}</span>
        </div>
      )}

      {/* ── Error state ────────────────────────────────────────────────── */}
      {tbError && selectedOrgId && !isLoading && (
        <div className="tb-center-state tb-err tb-anim-2">
          <AlertTriangle size={24} />
          <span>{t('platform:journal.trialBalance.errorText')}</span>
        </div>
      )}

      {/* ── Summary cards ──────────────────────────────────────────────── */}
      {trialBalance && !isLoading && (
        <>
          <div className="tb-summary-grid tb-anim-2">
            {/* Grand Total Debit */}
            <div className="tb-summary-card">
              <div className="tb-summary-header">
                <div className="tb-summary-icon tb-summary-icon--debit">
                  <TrendingUp size={18} />
                </div>
                <span className="tb-summary-label">{t('platform:journal.trialBalance.stats.grandDebit')}</span>
              </div>
              <div className="tb-summary-value">{formatMoney(trialBalance.grandTotalDebit)}</div>
            </div>

            {/* Grand Total Credit */}
            <div className="tb-summary-card">
              <div className="tb-summary-header">
                <div className="tb-summary-icon tb-summary-icon--credit">
                  <TrendingDown size={18} />
                </div>
                <span className="tb-summary-label">{t('platform:journal.trialBalance.stats.grandCredit')}</span>
              </div>
              <div className="tb-summary-value">{formatMoney(trialBalance.grandTotalCredit)}</div>
            </div>

            {/* Net Balance */}
            <div className="tb-summary-card">
              <div className="tb-summary-header">
                <div className="tb-summary-icon tb-summary-icon--net">
                  <Equal size={18} />
                </div>
                <span className="tb-summary-label">{t('platform:journal.trialBalance.stats.netBalance')}</span>
              </div>
              <div className={`tb-summary-value ${grandNetBalance < 0 ? 'tb-summary-value--negative' : ''}`}>
                {formatMoney(grandNetBalance)}
              </div>
            </div>

            {/* Balanced status */}
            <div className={`tb-summary-card tb-summary-card--status ${trialBalance.balanced ? 'tb-summary-card--balanced' : 'tb-summary-card--imbalanced'}`}>
              <div className="tb-summary-header">
                <div className={`tb-summary-icon ${trialBalance.balanced ? 'tb-summary-icon--balanced' : 'tb-summary-icon--imbalanced'}`}>
                  {trialBalance.balanced ? <CheckCircle size={18} /> : <XCircle size={18} />}
                </div>
                <span className="tb-summary-label">{t('platform:journal.trialBalance.stats.balanced')}</span>
              </div>
              <div className="tb-summary-status-text">
                {trialBalance.balanced
                  ? t('platform:journal.trialBalance.stats.balancedYes')
                  : t('platform:journal.trialBalance.stats.balancedNo')
                }
              </div>
            </div>
          </div>

          {/* ── Trial balance table ──────────────────────────────────────── */}
          <div className="tb-table-section tb-anim-3">
            <h2 className="tb-section-title">
              {t('platform:journal.trialBalance.accountBalances')}
              <span className="tb-account-count">{trialBalance.accounts.length} {t('platform:journal.trialBalance.accounts')}</span>
            </h2>
            <div className="tb-table-wrap">
              <table className="tb-table">
                <thead>
                  <tr>
                    <th className="tb-th-code">{t('platform:journal.trialBalance.table.accountCode')}</th>
                    <th className="tb-th-name">{t('platform:journal.trialBalance.table.accountName')}</th>
                    <th className="tb-th-type">{t('platform:journal.trialBalance.table.accountType')}</th>
                    <th className="tb-th-amount">{t('platform:journal.trialBalance.table.totalDebit')}</th>
                    <th className="tb-th-amount">{t('platform:journal.trialBalance.table.totalCredit')}</th>
                    <th className="tb-th-amount">{t('platform:journal.trialBalance.table.netBalance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {trialBalance.accounts.length === 0 && (
                    <tr>
                      <td colSpan={6} className="tb-table-empty">
                        {t('platform:journal.trialBalance.noAccounts')}
                      </td>
                    </tr>
                  )}
                  {trialBalance.accounts.map((account: AccountBalance) => {
                    const typeColor = getAccountTypeColor(account.accountType);
                    return (
                      <tr
                        key={account.accountId}
                        className={account.netBalance < 0 ? 'tb-row--negative' : ''}
                      >
                        <td className="tb-td-code">{account.code}</td>
                        <td className="tb-td-name">{account.name}</td>
                        <td className="tb-td-type">
                          <span
                            className="tb-type-badge"
                            style={{ background: typeColor.bg, color: typeColor.text }}
                          >
                            {account.accountType.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="tb-td-amount">{formatMoney(account.totalDebit)}</td>
                        <td className="tb-td-amount">{formatMoney(account.totalCredit)}</td>
                        <td className={`tb-td-amount tb-td-net ${account.netBalance < 0 ? 'tb-td-net--negative' : ''}`}>
                          {formatMoney(account.netBalance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {trialBalance.accounts.length > 0 && (
                  <tfoot>
                    <tr className="tb-grand-total-row">
                      <td className="tb-td-grand-label" colSpan={3}>
                        {t('platform:journal.trialBalance.table.grandTotal')}
                      </td>
                      <td className="tb-td-grand-amount">{formatMoney(trialBalance.grandTotalDebit)}</td>
                      <td className="tb-td-grand-amount">{formatMoney(trialBalance.grandTotalCredit)}</td>
                      <td className={`tb-td-grand-amount ${grandNetBalance < 0 ? 'tb-td-net--negative' : ''}`}>
                        {formatMoney(grandNetBalance)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* ── As-of date info ──────────────────────────────────────────── */}
          <div className="tb-footer-info tb-anim-4">
            <Calendar size={13} />
            <span>
              {t('platform:journal.trialBalance.reportAsOf')}{' '}
              <strong>{trialBalance.asOf}</strong>
            </span>
          </div>
        </>
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const pageStyles = `
  /* Page */
  .tb-page {
    display: flex; flex-direction: column; gap: 1.25rem;
    padding: 1.5rem 2rem; width: 100%; box-sizing: border-box;
  }

  /* Header */
  .tb-header {
    display: flex; justify-content: space-between; align-items: center;
    animation: tb-in-down 0.35s cubic-bezier(0.22,1,0.36,1) both;
  }
  .tb-header-left {
    display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
  }
  .tb-title {
    font: 700 1.4rem/1.2 var(--font-heading); color: var(--color-text-primary);
    display: flex; align-items: center; gap: 0.6rem; margin: 0;
  }
  .tb-title-icon { color: var(--color-brand-primary); }

  .tb-balanced-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: var(--radius-full);
    font-size: 12px; font-weight: 600;
  }
  .tb-balanced-pill--yes {
    background: var(--color-success-light); color: var(--color-success);
  }
  .tb-balanced-pill--no {
    background: var(--color-error-light); color: var(--color-error);
  }

  .tb-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 7px 14px; font-size: 13px; font-weight: 500;
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
    background: var(--color-bg-secondary); color: var(--color-text-primary);
    cursor: pointer; transition: all 0.15s;
  }
  .tb-btn:hover { background: var(--color-bg-tertiary); border-color: var(--color-border-strong); }

  /* Filters */
  .tb-filters {
    display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap;
    padding: 1rem 1.25rem; border-radius: var(--radius-lg);
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    box-shadow: var(--shadow-sm);
  }
  .tb-filter-group {
    display: flex; flex-direction: column; gap: 0.35rem; flex: 1; min-width: 200px;
  }
  .tb-filter-label {
    display: flex; align-items: center; gap: 0.35rem;
    font-size: 12px; font-weight: 600; color: var(--color-text-secondary);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .tb-select, .tb-date-input {
    padding: 8px 12px; font-size: 13px;
    border: 1px solid var(--input-border); border-radius: var(--radius-md);
    background: var(--input-bg); color: var(--color-text-primary);
    outline: none; transition: border-color 0.15s, box-shadow 0.15s;
    font-family: var(--font-body);
  }
  .tb-select:focus, .tb-date-input:focus {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px var(--input-focus-ring);
  }
  .tb-select { cursor: pointer; max-width: 100%; }

  /* Empty / center states */
  .tb-center-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.5rem; padding: 4rem; color: var(--color-text-secondary);
  }
  .tb-err { color: var(--color-error); }

  .tb-empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 1rem; padding: 4rem 2rem;
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
  }
  .tb-empty-icon { color: var(--color-text-muted); opacity: 0.5; }
  .tb-empty-text {
    font-size: 14px; color: var(--color-text-muted); margin: 0;
    text-align: center;
  }

  /* Summary cards */
  .tb-summary-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem;
  }
  .tb-summary-card {
    padding: 1rem 1.15rem; border-radius: var(--radius-lg);
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    box-shadow: var(--shadow-sm);
    display: flex; flex-direction: column; gap: 0.6rem;
  }
  .tb-summary-header {
    display: flex; align-items: center; gap: 0.5rem;
  }
  .tb-summary-icon {
    width: 34px; height: 34px; border-radius: var(--radius-md);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .tb-summary-icon--debit {
    background: var(--color-info-light); color: var(--color-info);
  }
  .tb-summary-icon--credit {
    background: var(--color-warning-light); color: var(--color-warning);
  }
  .tb-summary-icon--net {
    background: var(--color-bg-tertiary); color: var(--color-text-secondary);
  }
  .tb-summary-icon--balanced {
    background: var(--color-success-light); color: var(--color-success);
  }
  .tb-summary-icon--imbalanced {
    background: var(--color-error-light); color: var(--color-error);
  }
  .tb-summary-label {
    font-size: 12px; font-weight: 600; color: var(--color-text-secondary);
    text-transform: uppercase; letter-spacing: 0.03em;
  }
  .tb-summary-value {
    font: 700 1.5rem var(--font-heading); color: var(--color-text-primary);
    line-height: 1; padding-left: 0.15rem;
  }
  .tb-summary-value--negative { color: var(--color-error); }

  /* Balanced status card */
  .tb-summary-card--status { justify-content: center; }
  .tb-summary-card--balanced {
    background: var(--color-success-light);
    border-color: var(--color-success);
  }
  .tb-summary-card--imbalanced {
    background: var(--color-error-light);
    border-color: var(--color-error);
  }
  .tb-summary-status-text {
    font-size: 13px; font-weight: 600; padding-left: 0.15rem;
  }
  .tb-summary-card--balanced .tb-summary-status-text { color: var(--color-success); }
  .tb-summary-card--imbalanced .tb-summary-status-text { color: var(--color-error); }
  .tb-summary-card--balanced .tb-summary-label { color: var(--color-success); }
  .tb-summary-card--imbalanced .tb-summary-label { color: var(--color-error); }

  /* Section title */
  .tb-section-title {
    font: 600 0.82rem/1 var(--font-heading); color: var(--color-text-secondary);
    text-transform: uppercase; letter-spacing: 0.06em;
    margin: 0 0 0.65rem 0; display: flex; align-items: center; gap: 0.5rem;
  }
  .tb-account-count {
    font-size: 10.5px; padding: 1px 7px; border-radius: var(--radius-sm);
    background: var(--color-bg-tertiary); color: var(--color-text-muted);
    font-weight: 600; text-transform: none; letter-spacing: 0;
  }

  /* Table */
  .tb-table-section { }
  .tb-table-wrap {
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm);
  }
  .tb-table {
    width: 100%; border-collapse: collapse;
  }
  .tb-table thead th {
    text-align: left; padding: 0.65rem 0.85rem; font-size: 10.5px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted);
    background: var(--color-bg-tertiary); border-bottom: 1px solid var(--color-border-subtle);
  }
  .tb-th-code { width: 120px; }
  .tb-th-name { }
  .tb-th-type { width: 160px; }
  .tb-th-amount { width: 140px; text-align: right !important; }
  .tb-table tbody td {
    padding: 0.6rem 0.85rem; font-size: 13px; color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .tb-table tbody tr:last-child td { border-bottom: none; }
  .tb-table tbody tr:hover { background: var(--color-bg-tertiary); }

  /* Negative row tint */
  .tb-row--negative {
    background: color-mix(in srgb, var(--color-error) 4%, transparent);
  }
  .tb-row--negative:hover {
    background: color-mix(in srgb, var(--color-error) 8%, transparent) !important;
  }

  /* Table cells */
  .tb-td-code {
    font-family: monospace; font-size: 12.5px; font-weight: 600;
    color: var(--color-brand-primary);
  }
  .tb-td-name { font-weight: 500; }
  .tb-td-type { }
  .tb-td-amount { text-align: right; font-family: var(--font-heading); font-weight: 500; }
  .tb-td-net { font-weight: 700; }
  .tb-td-net--negative { color: var(--color-error); }

  /* Account type badge */
  .tb-type-badge {
    display: inline-block; padding: 2px 8px; border-radius: var(--radius-sm);
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
    white-space: nowrap;
  }

  /* Grand total row */
  .tb-grand-total-row {
    background: var(--color-bg-tertiary) !important;
  }
  .tb-grand-total-row:hover {
    background: var(--color-bg-tertiary) !important;
  }
  .tb-td-grand-label {
    padding: 0.75rem 0.85rem !important; font-size: 13px !important;
    font-weight: 700 !important; color: var(--color-text-primary) !important;
    text-transform: uppercase; letter-spacing: 0.04em;
    border-top: 2px solid var(--color-border-strong) !important;
    border-bottom: none !important;
  }
  .tb-td-grand-amount {
    padding: 0.75rem 0.85rem !important; text-align: right;
    font: 700 14px var(--font-heading); color: var(--color-text-primary);
    border-top: 2px solid var(--color-border-strong) !important;
    border-bottom: none !important;
  }

  /* Footer info */
  .tb-footer-info {
    display: flex; align-items: center; gap: 0.4rem;
    font-size: 12px; color: var(--color-text-muted);
    padding: 0.35rem 0;
  }
  .tb-footer-info strong {
    color: var(--color-text-secondary);
  }

  /* Staggered entrance animations */
  .tb-anim-1 { animation: tb-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.04s both; }
  .tb-anim-2 { animation: tb-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.08s both; }
  .tb-anim-3 { animation: tb-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.12s both; }
  .tb-anim-4 { animation: tb-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.16s both; }

  @keyframes tb-in-down {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes tb-in-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes tb-spin-kf {
    to { transform: rotate(360deg); }
  }
  .tb-spin { animation: tb-spin-kf 1s linear infinite; }

  .tb-table-empty {
    text-align: center; color: var(--color-text-muted);
    padding: 2.5rem 1rem !important; font-size: 13px;
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .tb-summary-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 640px) {
    .tb-page { padding: 1rem; }
    .tb-header { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
    .tb-filters { flex-direction: column; }
    .tb-filter-group { min-width: 0; }
    .tb-summary-grid { grid-template-columns: 1fr; }
    .tb-th-type { display: none; }
    .tb-td-type { display: none; }
    .tb-th-code { width: 80px; }
    .tb-th-amount { width: 100px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .tb-header, .tb-anim-1, .tb-anim-2, .tb-anim-3, .tb-anim-4, .tb-spin {
      animation: none !important;
      transition: none !important;
    }
  }
`;

export default TrialBalancePage;
